import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate, unwrapDate } from '~/types/dates'
import { type DomainGoalId, newId, type ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssue, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'
import { validateWeeklySchedule, type WeeklySchedule } from './trainingSchedule'

/**
 * Fitness domains and the domain goal (D-090).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A DOMAIN IS OPT-IN, AND NOTHING HERE ENROLS THE USER
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Body composition stops being the goal model and becomes one domain among
 *  four. **Only an active domain creates a goal, collects evidence, appears on
 *  Today or takes part in the weekly review.** A domain the user did not choose
 *  has no record at all — see `backfillDomains.ts` for why an `inactive` row is
 *  worse than an absent one.
 *
 *  Project Sola may *recommend* a supporting goal. It may never activate one,
 *  and connecting a wearable never activates a domain. That is D-020's rule
 *  applied one level further up than the project.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  "GOAL" MEANS TWO THINGS AND THEY ARE NAMED DIFFERENTLY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `GoalRecord` in `goal.ts` is the **body-composition target** — target weight
 *  and waist ranges. `DomainGoalRecord` here is the **domain goal**. The bare
 *  word "goal" is never used alone in code, documents or interface copy, because
 *  "Checkpoint 2.5" naming two things already cost this project a documented
 *  hazard.
 *
 *  The same applies to strength, which also appears twice: `strengthConcern` in
 *  `rules/rulesets/shared.ts` is **strength-as-guardrail**, fires inside the
 *  body-composition opinion, and is unchanged by this file.
 *  **Strength-as-improvement-domain** is a domain goal and has no ruleset.
 */

export const FITNESS_DOMAINS = ['bodyComposition', 'cardio', 'strength', 'mobility'] as const

export type FitnessDomain = (typeof FITNESS_DOMAINS)[number]

/** Display labels. Presentation only — never persisted, never compared. */
export const FITNESS_DOMAIN_LABELS: Readonly<Record<FitnessDomain, string>> = {
  bodyComposition: 'Body composition',
  cardio: 'Cardio endurance',
  strength: 'Strength',
  mobility: 'Mobility',
}

export function isFitnessDomain(value: string): value is FitnessDomain {
  return (FITNESS_DOMAINS as readonly string[]).includes(value)
}

/**
 * What the user wants this domain to do.
 *
 * `inactive` is representable so that a *stored* record can be switched off
 * without being deleted — a domain the user tried and turned off keeps its
 * history. It is **never** what a fresh install or a backfill writes; an absent
 * record is the normal way a domain is off.
 *
 * `trackOnly` is not a weaker `maintain`. It means "collect the evidence and
 * show it to me, and do not plan anything" — no weekly commitment, no target,
 * and no commentary in the weekly review.
 */
export const DOMAIN_INTENTIONS = ['improve', 'maintain', 'trackOnly', 'inactive'] as const

export type DomainIntention = (typeof DOMAIN_INTENTIONS)[number]

export const DOMAIN_INTENTION_LABELS: Readonly<Record<DomainIntention, string>> = {
  improve: 'Improve',
  maintain: 'Maintain',
  trackOnly: 'Track only',
  inactive: 'Not active',
}

export function isDomainIntention(value: string): value is DomainIntention {
  return (DOMAIN_INTENTIONS as readonly string[]).includes(value)
}

/**
 * Where this domain sits against the others.
 *
 * Ordering, never weights — the same shape as `RULE_FAMILY_PRECEDENCE`. One
 * primary and optionally one supporting is the recommended configuration and is
 * **not** enforced here: the user decides, and `validateDomainGoal` checks only
 * that a record is internally coherent. Whether two primaries may coexist is a
 * question for the service that writes a set of them, not for one record.
 */
export const DOMAIN_PRIORITIES = ['primary', 'supporting', 'protect', 'none'] as const

export type DomainPriority = (typeof DOMAIN_PRIORITIES)[number]

export const DOMAIN_PRIORITY_LABELS: Readonly<Record<DomainPriority, string>> = {
  primary: 'Primary goal',
  supporting: 'Supporting goal',
  protect: 'Protect',
  none: 'No priority',
}

export function isDomainPriority(value: string): value is DomainPriority {
  return (DOMAIN_PRIORITIES as readonly string[]).includes(value)
}

/**
 * A domain goal's lifecycle.
 *
 * `paused` is distinct from an `inactive` intention: pausing is a temporary
 * suspension that keeps the plan intact and stops asking about it, and a paused
 * domain's already-collected answers are retained, not re-asked and **not
 * judged**. Ending it is the deliberate decision.
 */
export const DOMAIN_STATUSES = ['active', 'paused', 'ended'] as const

export type DomainStatus = (typeof DOMAIN_STATUSES)[number]

export function isDomainStatus(value: string): value is DomainStatus {
  return (DOMAIN_STATUSES as readonly string[]).includes(value)
}

/**
 * Which way this domain is meant to move.
 *
 * Separate from `intention` because they answer different questions: intention
 * is how much the app should do about it, direction is what "better" means.
 * `restore` exists because returning to a previous level after injury or a break
 * is neither improvement from a baseline nor maintenance of the current one.
 */
export const DOMAIN_DIRECTIONS = ['improve', 'maintain', 'restore'] as const

export type DomainDirection = (typeof DOMAIN_DIRECTIONS)[number]

export function isDomainDirection(value: string): value is DomainDirection {
  return (DOMAIN_DIRECTIONS as readonly string[]).includes(value)
}

/**
 * Whether the app knows where this domain is starting from.
 *
 * `baselineRequired` is an honest answer, not a gap to be filled with a guess.
 * A domain goal must never require the user to know a numeric target, so a goal
 * whose baseline is unknown is a perfectly valid, plannable goal — it simply
 * collects evidence first and states so on screen.
 */
export const BASELINE_STATES = ['known', 'baselineRequired'] as const

export type BaselineState = (typeof BASELINE_STATES)[number]

export function isBaselineState(value: string): value is BaselineState {
  return (BASELINE_STATES as readonly string[]).includes(value)
}

/**
 * The kinds of evidence a domain goal declares it will collect.
 *
 * A stable vocabulary rather than free text, so a screen can ask "does this goal
 * need a weigh-in today?" without inspecting the domain. **Declaring an evidence
 * kind is not the same as a rule reading it** — D-060 forbids a rule whose
 * evidence is uncollected, and this list is the collection half only.
 */
export const EVIDENCE_KINDS = [
  'weighIn',
  'circumference',
  'photo',
  'strengthSet',
  'cardioSession',
  'mobilitySession',
  'nutritionCheck',
  'recoveryCheck',
  'wearableDailySummary',
] as const

export type EvidenceKind = (typeof EVIDENCE_KINDS)[number]

export function isEvidenceKind(value: string): value is EvidenceKind {
  return (EVIDENCE_KINDS as readonly string[]).includes(value)
}

/**
 * One thing the user plans to do in a week.
 *
 * `minutes` is an honest time estimate rather than a prescription, and it is
 * optional because not every item has one worth stating.
 */
export interface PlannedItem {
  readonly label: string
  readonly timesPerWeek: number
  readonly minutes?: number
}

/**
 * The three versions of a week, as data rather than as copy.
 *
 * A low-energy alternative that lives in a paragraph is a suggestion; one that
 * lives here is something the app can actually offer, count and review against.
 * **`minimumViable` is never empty while the goal is active** — a goal with no
 * floor has no low-energy path, which is the failure this field exists to
 * prevent. `validateDomainGoal` enforces that.
 *
 * **`standard` became derived at D-105 §3 and `short` and `minimumViable` did
 * not.** Once a goal has a `weeklySchedule`, its standard week comes from that
 * schedule through `standardWeekFor` and is not persisted here — see the note on
 * `scheduleAuthorityIssues`. The other two stay separately editable and carry no
 * weekdays on purpose: they are *descriptions of a week* rather than calendars
 * (D-099 §C), and forcing weekdays onto `minimumViable` would destroy the floor
 * D-101 §4 requires never be empty.
 */
export interface WeeklyCommitment {
  readonly standard: readonly PlannedItem[]
  readonly short: readonly PlannedItem[]
  readonly minimumViable: readonly PlannedItem[]
}

/**
 * An optional numeric target.
 *
 * Optional is the load-bearing word: a goal must not require the user to know a
 * number. `unit` is a display unit and the value is stored as entered, because
 * these targets span domains with no shared canonical unit — minutes, sessions,
 * kilograms, a count of repetitions.
 */
export interface DomainNumericTarget {
  readonly value: number
  readonly unit: string
  readonly describe: string
}

/**
 * One recorded change to a domain goal.
 *
 * Append-only, following `PhaseCorrection` exactly. Pausing, resuming,
 * re-prioritising and ending all append rather than overwrite, which is how
 * "changing active goals preserves history" is made structural instead of
 * remembered.
 */
export type ChangeableDomainField =
  | 'intention'
  | 'priority'
  | 'status'
  | 'direction'
  | 'reassessOn'
  | 'weeklyCommitment'
  /* Joined at C7.5 for the same reason `weeklyCommitment` joined at C6.5: the
     schedule is plan data, My Plan is the only place it may change, and an edit
     with no provenance is a week nobody can explain afterwards (D-105 §7). */
  | 'weeklySchedule'

/**
 * Where a change came from, when it did not come from the user (D-093 §6).
 *
 * **Absent means the user made it**, which is why there is no `'user'` member: an
 * optional field carrying a value that means "nothing unusual" would have to be
 * written onto every existing record to be trustworthy, and it is not. A present
 * value is a positive claim about a specific origin.
 *
 * **This is a provenance label, not a permission.** A proposal never writes a
 * record — it pre-fills the editor and the user saves it — so a change tagged
 * `geminiProposal` is still a change the user made, with the reviewer's numbers
 * in it. That distinction is the whole of D-093 §6 and the reason the honest cost
 * is stated there rather than buried: a pre-filled field is usually just saved.
 */
export const DOMAIN_CHANGE_SOURCES = ['geminiProposal'] as const

export type DomainChangeSource = (typeof DOMAIN_CHANGE_SOURCES)[number]

export function isDomainChangeSource(value: string): value is DomainChangeSource {
  return (DOMAIN_CHANGE_SOURCES as readonly string[]).includes(value)
}

export interface DomainGoalChange {
  readonly field: ChangeableDomainField
  /**
   * The previous and next values, **where they are expressible as a word**.
   *
   * A `weeklyCommitment` change leaves both absent, and that is deliberate rather
   * than an omission. A commitment is a structured object; serializing one into
   * this string would invent a second history shape whose only reader would be a
   * parser nobody maintains, and it still would not answer the question people
   * actually ask — *what was I supposed to be doing that week?*
   *
   * **The authoritative answer to that is `WeeklyReviewRecord.planSnapshot`**,
   * frozen when the week was finalized (D-099 as amended at C6.5). This entry
   * records that a change happened, when, and from which week it applies.
   */
  readonly from?: string
  readonly to?: string
  readonly changedAt: IsoTimestamp
  /**
   * The first week this change applies to.
   *
   * A commitment change takes effect at the start of the next week, never
   * mid-week — moving the target under a week already in progress is how a
   * description of a week becomes something to fail.
   */
  readonly effectiveFrom?: CalendarDate
  readonly reason?: string
  /**
   * Whose numbers these were, when they were not the user's own.
   *
   * Absent on every change made the ordinary way, and on every record written
   * before C7.0 phase 5 — which needs no migration precisely because absent is
   * the normal case rather than a gap.
   */
  readonly source?: DomainChangeSource
}

export interface DomainGoalRecord extends RecordEnvelope {
  readonly id: DomainGoalId
  readonly projectId: ProjectId
  readonly domain: FitnessDomain
  readonly intention: DomainIntention
  readonly priority: DomainPriority
  readonly status: DomainStatus
  readonly direction: DomainDirection
  readonly baseline: BaselineState
  readonly evidenceKinds: readonly EvidenceKind[]
  readonly weeklyCommitment?: WeeklyCommitment
  /**
   * This domain's recurring week (D-105 §7).
   *
   * **Each recurring session belongs to exactly one domain goal**, and this is
   * the editable authority for that domain's standard week. My Plan composes
   * every participating domain into one chronological editor, which is a *view*
   * over these per-domain authorities and never a separate persisted schedule —
   * a project-level schedule would leave orphan sessions pointing at an ended
   * goal, and would invert the ownership D-090 established.
   *
   * Absent means the user has not created one. It is never an empty array
   * standing in for "none", for the same reason an inactive domain has no record
   * at all (D-092): an empty schedule would assert a choice nobody made.
   */
  readonly weeklySchedule?: WeeklySchedule
  readonly numericTarget?: DomainNumericTarget
  /** When to look at this again. A date the user or the app chose. */
  readonly reassessOn?: CalendarDate
  /**
   * A condition to reassess on, where no date fits.
   *
   * "Once you have four weeks of cardio sessions recorded" is a real answer for
   * a goal whose baseline is unknown, and a date would be a fabrication.
   */
  readonly reassessWhen?: string
  readonly startedOn: CalendarDate
  readonly endedOn?: CalendarDate
  readonly changes: readonly DomainGoalChange[]
  readonly notes?: string
}

export interface DomainGoalInput {
  readonly projectId: string
  readonly domain: string
  readonly intention: string
  readonly priority: string
  readonly direction: string
  readonly baseline: string
  readonly evidenceKinds: readonly string[]
  readonly startedOn: string
  readonly weeklyCommitment?: WeeklyCommitment
  readonly weeklySchedule?: WeeklySchedule
  readonly numericTarget?: DomainNumericTarget
  readonly reassessOn?: string
  readonly reassessWhen?: string
  readonly notes?: string
}

const MAX_NOTE_LENGTH = 500

export function validateDomainGoal(input: DomainGoalInput): ValidationIssues {
  const issues: ValidationIssue[] = [
    ...vocabularyIssues(input),
    ...dateIssues(input),
    ...commitmentIssues(input),
    ...scheduleIssues(input),
  ]

  if (input.numericTarget !== undefined && !Number.isFinite(input.numericTarget.value)) {
    issues.push(issue('numericTarget', 'A numeric target must be a real number.'))
  }

  if (input.notes !== undefined && input.notes.length > MAX_NOTE_LENGTH) {
    issues.push(issue('notes', `A note may be at most ${MAX_NOTE_LENGTH} characters.`))
  }

  return issues
}

/**
 * Every field whose value must come from a stable vocabulary.
 *
 * Table-driven rather than six `if`s so adding a vocabulary field is one row.
 * Each entry names the field, its guard and its own list, and the message is
 * built from the list itself so it cannot drift from what is actually accepted.
 */
function vocabularyIssues(input: DomainGoalInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (input.projectId.trim().length === 0) {
    issues.push(issue('projectId', 'A domain goal must belong to a project.'))
  }

  const checks: readonly {
    readonly field: string
    readonly value: string
    readonly accept: (value: string) => boolean
    readonly allowed: readonly string[]
  }[] = [
    { field: 'domain', value: input.domain, accept: isFitnessDomain, allowed: FITNESS_DOMAINS },
    {
      field: 'intention',
      value: input.intention,
      accept: isDomainIntention,
      allowed: DOMAIN_INTENTIONS,
    },
    {
      field: 'priority',
      value: input.priority,
      accept: isDomainPriority,
      allowed: DOMAIN_PRIORITIES,
    },
    {
      field: 'direction',
      value: input.direction,
      accept: isDomainDirection,
      allowed: DOMAIN_DIRECTIONS,
    },
    { field: 'baseline', value: input.baseline, accept: isBaselineState, allowed: BASELINE_STATES },
  ]

  for (const check of checks) {
    if (!check.accept(check.value)) {
      issues.push(issue(check.field, `${check.field} must be one of: ${check.allowed.join(', ')}.`))
    }
  }

  for (const kind of input.evidenceKinds) {
    if (!isEvidenceKind(kind)) {
      issues.push(issue('evidenceKinds', `"${kind}" is not a recognised kind of evidence.`))
    }
  }

  return issues
}

/**
 * Start and reassessment dates.
 *
 * A reassessment date before the start is refused rather than ignored: it would
 * put the next look at the goal in the past, and a screen sorting by it would put
 * the goal permanently at the top of the list.
 */
function dateIssues(input: DomainGoalInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  const startedOn = toCalendarDate(input.startedOn)
  if (startedOn === undefined) {
    issues.push(issue('startedOn', 'Start date must be a real date in YYYY-MM-DD form.'))
  }

  if (input.reassessOn === undefined) return issues

  const reassessOn = toCalendarDate(input.reassessOn)
  if (reassessOn === undefined) {
    issues.push(issue('reassessOn', 'Reassessment date must be a real date in YYYY-MM-DD form.'))
    return issues
  }
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  if (startedOn !== undefined && unwrapDate(reassessOn) < unwrapDate(startedOn)) {
    issues.push(issue('reassessOn', 'Reassessment date must not be before the start date.'))
  }

  return issues
}

/**
 * The minimum-viable floor, and why it is validated rather than defaulted.
 *
 * A `trackOnly` goal plans nothing, so it has no commitment and needs no floor.
 * Any goal that *does* carry a commitment must carry a non-empty
 * `minimumViable`, because the whole point of the field is that the low-energy
 * path exists. Filling it in with a copy of `short` would make the guarantee
 * true and meaningless.
 */
function commitmentIssues(input: DomainGoalInput): ValidationIssues {
  const commitment = input.weeklyCommitment
  if (commitment === undefined) return []
  return validateWeeklyCommitment(commitment)
}

function scheduleIssues(input: DomainGoalInput): ValidationIssues {
  const schedule = input.weeklySchedule
  if (schedule === undefined) return []
  return [
    ...validateWeeklySchedule(schedule),
    ...scheduleAuthorityIssues(schedule, input.weeklyCommitment),
  ]
}

/**
 * One authority for the standard week, enforced rather than described.
 *
 * D-105 §3: the schedule is the sole editable authority, and
 * `weeklyCommitment.standard` **must never be persisted as an independently
 * editable value** once one exists. So a record carrying both is refused here
 * rather than tolerated and quietly preferred by whichever reader looked first —
 * that is the two-representations shape D-099 §D rejected, and the way it
 * arrives is nobody deciding to build it.
 *
 * **A goal with no schedule keeps its stored `standard` and is untouched.** That
 * is existing plan data and remains valid until the user creates a schedule,
 * which supersedes it from the next week boundary. Nothing backfills weekdays
 * onto it: inventing the day an existing commitment happens on is D-101 §3's
 * forbidden column.
 *
 * `short` and `minimumViable` are unaffected, so the floor survives the
 * transition — which is the point of validating this narrowly rather than
 * rejecting a commitment outright.
 */
function scheduleAuthorityIssues(
  schedule: WeeklySchedule,
  commitment: WeeklyCommitment | undefined,
): ValidationIssues {
  if (schedule.length === 0) return []
  if (commitment === undefined || commitment.standard.length === 0) return []
  return [
    issue(
      'weeklySchedule',
      'A standard week is derived from the schedule and cannot also be written by hand.',
    ),
  ]
}

/**
 * Validate a weekly commitment on its own.
 *
 * Extracted from `commitmentIssues` so `applyDomainGoalTransition` can validate
 * too. The commitment is the one changeable field that arrives as structured
 * data rather than as a word from a fixed vocabulary, so it is the only one a
 * transition can be handed in a shape that is invalid — the other three cannot
 * be, which is why that path never needed to check anything before.
 */
export function validateWeeklyCommitment(commitment: WeeklyCommitment): ValidationIssues {
  const issues: ValidationIssue[] = []
  if (commitment.minimumViable.length === 0) {
    issues.push(
      issue(
        'weeklyCommitment',
        'A weekly plan must include a minimum-viable version, so there is always a smaller thing to do.',
      ),
    )
  }

  const everyItem = [...commitment.standard, ...commitment.short, ...commitment.minimumViable]
  for (const item of everyItem) {
    if (item.label.trim().length === 0) {
      issues.push(issue('weeklyCommitment', 'Every planned item needs a label.'))
    }
    if (!Number.isInteger(item.timesPerWeek) || item.timesPerWeek < 1) {
      issues.push(
        issue(
          'weeklyCommitment',
          'A planned item must happen at least once in a week, as a whole number.',
        ),
      )
    }
    if (item.minutes !== undefined && (!Number.isFinite(item.minutes) || item.minutes <= 0)) {
      issues.push(
        issue('weeklyCommitment', 'A time estimate must be a positive number of minutes.'),
      )
    }
  }

  return issues
}

function samePlannedItems(left: readonly PlannedItem[], right: readonly PlannedItem[]): boolean {
  if (left.length !== right.length) return false
  return left.every((item, index) => {
    const other = right[index]
    return (
      other !== undefined &&
      item.label === other.label &&
      item.timesPerWeek === other.timesPerWeek &&
      item.minutes === other.minutes
    )
  })
}

/**
 * Whether two commitments say the same thing.
 *
 * Compared field by field rather than through `JSON.stringify`, which answers a
 * different question: two objects with the same contents in a different key
 * order serialize differently, so a stringify comparison would report a change
 * nobody made and append a history entry for it. Order *within* a list is
 * significant, because the order items are planned in is the order they are
 * shown in.
 */
export function sameWeeklyCommitment(
  left: WeeklyCommitment | undefined,
  right: WeeklyCommitment | undefined,
): boolean {
  if (left === undefined || right === undefined) return left === right
  return (
    samePlannedItems(left.standard, right.standard) &&
    samePlannedItems(left.short, right.short) &&
    samePlannedItems(left.minimumViable, right.minimumViable)
  )
}

/**
 * Identity carried across an edit.
 *
 * The same reasoning as `ExistingProjectIdentity`: an edit must not mint a
 * second domain goal, resurrect an ended one, or drop the change history. The
 * caller supplies the stored identity and the builder preserves it verbatim.
 */
export interface ExistingDomainGoalIdentity {
  readonly id: DomainGoalId
  readonly createdAt: IsoTimestamp
  /**
   * The four fields that move only through `applyDomainGoalTransition`.
   *
   * They are carried here rather than read from the input because a transition is
   * the **only** path that appends to `changes`. An edit form that could also set
   * them would change a goal's priority with no record of it, which defeats the
   * whole point of the append-only history — and the two paths would both be able
   * to write the same field with only one of them accountable for it.
   *
   * **`weeklyCommitment` joined them at C6.5.** D-099 makes My Plan the only
   * place a commitment can change, which is a claim about accountability rather
   * than about screens: leaving it readable from the input would have let an
   * ordinary edit move it silently, and the first time anyone noticed would have
   * been a review whose plan nobody could explain.
   */
  readonly status: DomainStatus
  readonly priority: DomainPriority
  readonly intention: DomainIntention
  readonly weeklyCommitment?: WeeklyCommitment
  /* Joined the carried fields at C7.5, for the reason above: the schedule is
     accountable plan data, so only a transition may install one and an ordinary
     edit carries the stored value through untouched. */
  readonly weeklySchedule?: WeeklySchedule
  readonly changes: readonly DomainGoalChange[]
  readonly endedOn?: CalendarDate
}

function commitmentField(commitment: WeeklyCommitment | undefined): {
  weeklyCommitment?: WeeklyCommitment
} {
  return commitment === undefined ? {} : { weeklyCommitment: commitment }
}

function scheduleField(schedule: WeeklySchedule | undefined): {
  weeklySchedule?: WeeklySchedule
} {
  return schedule === undefined ? {} : { weeklySchedule: schedule }
}

/**
 * Whether two schedules say the same thing.
 *
 * Field by field and in order, exactly as `sameWeeklyCommitment` is and for the
 * same reason: `JSON.stringify` answers a different question and would append a
 * history entry for a change nobody made. **Order is compared because the array
 * is what the user arranged**, and moving a session within a day is a real edit.
 */
export function sameWeeklySchedule(
  left: WeeklySchedule | undefined,
  right: WeeklySchedule | undefined,
): boolean {
  if (left === undefined || right === undefined) return left === right
  if (left.length !== right.length) return false
  return left.every((session, index) => {
    const other = right[index]
    return (
      other !== undefined &&
      session.id === other.id &&
      session.weekday === other.weekday &&
      session.name === other.name &&
      session.expectedMinutes === other.expectedMinutes
    )
  })
}

/**
 * The two plan fields, which an ordinary edit carries rather than sets.
 *
 * Both follow the rule on `ExistingDomainGoalIdentity`: a first-run record may
 * arrive carrying them, and on an edit they come from the stored record so that
 * **only a transition installs a change and every change is accountable.** The
 * input's values are still validated either way, so a form submitting a
 * malformed plan is rejected rather than quietly ignored.
 */
function planFields(
  input: DomainGoalInput,
  existing: ExistingDomainGoalIdentity | undefined,
): { weeklyCommitment?: WeeklyCommitment; weeklySchedule?: WeeklySchedule } {
  return {
    ...commitmentField(existing === undefined ? input.weeklyCommitment : existing.weeklyCommitment),
    ...scheduleField(existing === undefined ? input.weeklySchedule : existing.weeklySchedule),
  }
}

/** Build a validated domain goal without touching storage. */
export function buildDomainGoalRecord(
  input: DomainGoalInput,
  now: IsoTimestamp,
  existing?: ExistingDomainGoalIdentity,
): Result<DomainGoalRecord, ValidationIssues> {
  const issues = validateDomainGoal(input)
  if (issues.length > 0) return err(issues)

  const startedOn = toCalendarDate(input.startedOn)
  if (startedOn === undefined) {
    return err([issue('domainGoal', 'Domain goal dates failed construction.')])
  }
  const reassessOn = input.reassessOn === undefined ? undefined : toCalendarDate(input.reassessOn)

  return ok({
    id: existing?.id ?? newId<'DomainGoalId'>(),
    projectId: input.projectId as ProjectId,
    domain: input.domain as FitnessDomain,
    /* On an edit these come from the stored record, not the input — see the note
       on `ExistingDomainGoalIdentity`. The input's values are still validated, so
       a form submitting a nonsense priority is rejected rather than ignored. */
    intention: existing?.intention ?? (input.intention as DomainIntention),
    priority: existing?.priority ?? (input.priority as DomainPriority),
    status: existing?.status ?? 'active',
    direction: input.direction as DomainDirection,
    baseline: input.baseline as BaselineState,
    evidenceKinds: input.evidenceKinds as readonly EvidenceKind[],
    ...planFields(input, existing),
    ...(input.numericTarget === undefined ? {} : { numericTarget: input.numericTarget }),
    ...(reassessOn === undefined ? {} : { reassessOn }),
    ...(input.reassessWhen === undefined ? {} : { reassessWhen: input.reassessWhen }),
    startedOn,
    /* Carried, not rebuilt: when and whether a domain goal ended is history, and
       an ordinary edit must not erase it. */
    ...(existing?.endedOn === undefined ? {} : { endedOn: existing.endedOn }),
    changes: existing?.changes ?? [],
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * The commitment as it stands once a schedule is in force.
 *
 * **Creating the first schedule supersedes the previous `standard`** (D-105 §3),
 * and this is where that happens — at the one write path, so a stored record can
 * never carry both authorities and `scheduleAuthorityIssues` never has to refuse
 * an ordinary save.
 *
 * `short` and `minimumViable` are carried through untouched. Dropping the whole
 * commitment would take the floor with it, and a goal with no `minimumViable` is
 * the failure D-101 §4 exists to prevent — the low-energy path is what makes a
 * bad week survivable rather than failed.
 *
 * The effective date is not this function's business: it rides on the change
 * entry, which the caller stamps with the start of next week (D-099 Amendment 2).
 */
function supersededCommitment(
  commitment: WeeklyCommitment | undefined,
  schedule: WeeklySchedule | undefined,
): WeeklyCommitment | undefined {
  if (schedule === undefined || schedule.length === 0) return commitment
  if (commitment === undefined || commitment.standard.length === 0) return commitment
  return { ...commitment, standard: [] }
}

/**
 * The two changeable fields that arrive as structured data.
 *
 * The four vocabulary fields cannot arrive malformed, which is why this path
 * never validated anything before C6.5. A commitment and a schedule both can,
 * and both are refused before anything is compared — so a transition never
 * reports a change it then declines to make.
 */
function structuredFieldIssues(next: {
  readonly weeklyCommitment?: WeeklyCommitment
  readonly weeklySchedule?: WeeklySchedule
}): ValidationIssues {
  return [
    ...(next.weeklyCommitment === undefined ? [] : validateWeeklyCommitment(next.weeklyCommitment)),
    ...(next.weeklySchedule === undefined ? [] : validateWeeklySchedule(next.weeklySchedule)),
  ]
}

/**
 * Apply a lifecycle transition, recording it.
 *
 * Returns the record unchanged and appends nothing when the transition is a
 * no-op, so the history shows changes that actually happened — the same rule as
 * a phase-history form saved without edits.
 *
 * There is deliberately no transition *out of* `ended`. Ending is the
 * deliberate decision, and resuming an ended goal would be a new goal with its
 * own start date rather than a revival of this one.
 */
export function applyDomainGoalTransition(
  record: DomainGoalRecord,
  next: {
    readonly status?: DomainStatus
    readonly priority?: DomainPriority
    readonly intention?: DomainIntention
    readonly weeklyCommitment?: WeeklyCommitment
    readonly weeklySchedule?: WeeklySchedule
  },
  now: IsoTimestamp,
  options: {
    readonly endedOn?: CalendarDate
    readonly effectiveFrom?: CalendarDate
    readonly reason?: string
    /* Stamped on every entry this transition appends. A single transition is one
       user action, so its entries share an origin — there is no case where the
       reviewer supplied the commitment and the user supplied the priority in the
       same call. */
    readonly source?: DomainChangeSource
  } = {},
): Result<DomainGoalRecord, ValidationIssues> {
  if (record.status === 'ended') {
    return err([
      issue(
        'status',
        'This goal has ended. Add a new goal for this domain instead of reopening it.',
      ),
    ])
  }

  const invalid = structuredFieldIssues(next)
  if (invalid.length > 0) return err(invalid)

  const changes: DomainGoalChange[] = []
  const entry = (field: ChangeableDomainField): DomainGoalChange => ({
    field,
    changedAt: now,
    ...(options.effectiveFrom === undefined ? {} : { effectiveFrom: options.effectiveFrom }),
    ...(options.reason === undefined ? {} : { reason: options.reason }),
    ...(options.source === undefined ? {} : { source: options.source }),
  })
  const append = (field: ChangeableDomainField, from: string, to: string): void => {
    if (from === to) return
    changes.push({ ...entry(field), from, to })
  }

  const status = next.status ?? record.status
  const priority = next.priority ?? record.priority
  const intention = next.intention ?? record.intention

  append('status', record.status, status)
  append('priority', record.priority, priority)
  append('intention', record.intention, intention)

  const schedule = next.weeklySchedule ?? record.weeklySchedule

  /* No `from`/`to` — see the note on `DomainGoalChange`. The snapshot on the
     weekly review is what answers "what was planned that week"; this entry
     answers "when did it change, and from which week". */
  const commitment = supersededCommitment(
    next.weeklyCommitment ?? record.weeklyCommitment,
    schedule,
  )
  if (!sameWeeklyCommitment(record.weeklyCommitment, commitment)) {
    changes.push(entry('weeklyCommitment'))
  }

  /* Structured like the commitment for the same reason, and a separate entry
     rather than a shared one: superseding a hand-written standard week is a
     consequence the history should show happening, not an invisible side effect
     of the schedule edit that caused it. */
  if (!sameWeeklySchedule(record.weeklySchedule, schedule)) {
    changes.push(entry('weeklySchedule'))
  }

  if (changes.length === 0) return ok(record)

  const endedOn = status === 'ended' ? (options.endedOn ?? record.endedOn) : record.endedOn

  return ok({
    ...record,
    status,
    priority,
    intention,
    ...commitmentField(commitment),
    ...scheduleField(schedule),
    ...(endedOn === undefined ? {} : { endedOn }),
    changes: [...record.changes, ...changes],
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/** Whether this goal takes part in tasks, evidence and the weekly review. */
export function isParticipating(record: DomainGoalRecord): boolean {
  return record.status === 'active' && record.intention !== 'inactive'
}

/**
 * Whether this goal is planned against, as opposed to merely observed.
 *
 * `trackOnly` collects evidence and is shown; it is never commented on in the
 * weekly review and never contributes a task. Keeping the two questions apart is
 * what stops a track-only domain generating work.
 */
export function isPlanned(record: DomainGoalRecord): boolean {
  return isParticipating(record) && record.intention !== 'trackOnly'
}
